// Event detail page. The viewer's own participant row drives the RSVP
// control the same way GroupPage's membership row drives join/leave; RLS
// enforces everything server-side, the UI only decides what to offer.
// An event out of visibility (e.g. INVITED to a members-only event of a club
// the viewer isn't in) still exposes the viewer's own eventParticipant row,
// so the "not found" path first checks for an invitation to render an
// accept/decline card — mirroring the hidden-club invite flow.
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { CalendarDays, CalendarPlus, MapPin, Compass } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  CREATE_EVENT_PARTICIPANT,
  DELETE_EVENT,
  DELETE_EVENT_PARTICIPANT,
  EVENT_DETAIL,
  MY_CLUB_MEMBERSHIPS,
  MY_EVENT_PARTICIPATIONS,
  UPDATE_EVENT_PARTICIPANT,
  type EventDetailResult,
  type EventParticipantNode,
  type MyClubMembershipsResult,
  type MyEventParticipationsResult,
  type PersonSummary,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { formatDate, formatDateRange, formatDuration, type EventParticipantStatus } from "../lib/format";
import { downloadEventIcs } from "../lib/ics";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { PersonPicker } from "../components/PersonPicker";
import { Avatar } from "../components/Avatar";
import { ShareButton } from "../components/ShareButton";
import { ActivityList, EmptyState, ErrorBox, Spinner } from "../components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MiniMap } from "../map/MiniMap";
import { toFeature, pointCoords } from "../lib/geo";

export function EventPage({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t, labelizeT, entityName } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["event", id],
    queryFn: () => gqlRequest<EventDetailResult>(EVENT_DETAIL, { id }),
  });

  // The viewer's own rows — needed both for the invited-but-hidden card and
  // as the myRow fallback (the detail list already includes it otherwise).
  const participations = useQuery({
    queryKey: ["my-event-participations", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyEventParticipationsResult>(MY_EVENT_PARTICIPATIONS, { personId }),
  });

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["event", id] });
    queryClient.invalidateQueries({ queryKey: ["my-event-participations"] });
  };

  const rsvp = useMutation({
    mutationFn: (status: "CONFIRMED" | "PENDING") =>
      gqlRequest(CREATE_EVENT_PARTICIPANT, { eventId: id, personId, role: "ATTENDEE", status }),
    onSuccess: invalidate,
  });

  const patchParticipant = useMutation({
    mutationFn: (vars: { participantId: string; status: EventParticipantStatus }) =>
      gqlRequest(UPDATE_EVENT_PARTICIPANT, { id: vars.participantId, patch: { status: vars.status } }),
    onSuccess: invalidate,
  });

  const removeParticipant = useMutation({
    mutationFn: (participantId: string) => gqlRequest(DELETE_EVENT_PARTICIPANT, { id: participantId }),
    onSuccess: invalidate,
  });

  const invite = useMutation({
    mutationFn: (p: PersonSummary) =>
      gqlRequest(CREATE_EVENT_PARTICIPANT, { eventId: id, personId: p.id, role: "ATTENDEE", status: "INVITED" }),
    onSuccess: invalidate,
  });

  const deleteEvent = useMutation({
    mutationFn: () => gqlRequest(DELETE_EVENT, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-event-participations"] });
      navigate("/");
    },
  });

  if (isLoading || (personId && (participations.isLoading || memberships.isLoading))) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const event = data?.event;
  const myParticipation = participations.data?.eventParticipants.nodes.find((p) => p.eventId === id) ?? null;

  if (!event) {
    // The event is out of visibility, but the viewer's own INVITED row is
    // not — offer accept/decline from it alone (hidden-club invite pattern).
    if (myParticipation?.status === "INVITED") {
      return (
        <div className="mx-auto m-4 max-w-md rounded-xl border bg-card p-6 text-center text-card-foreground shadow-card">
          <p className="mb-4 text-sm text-foreground">{t("event.invitedBanner")}</p>
          <div className="flex justify-center gap-3">
            <Button
              type="button"
              onClick={() => patchParticipant.mutate({ participantId: myParticipation.id, status: "CONFIRMED" })}
              disabled={patchParticipant.isPending}
            >
              {t("event.acceptInvite")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => patchParticipant.mutate({ participantId: myParticipation.id, status: "DECLINED" })}
              disabled={patchParticipant.isPending}
            >
              {t("group.decline")}
            </Button>
          </div>
          {patchParticipant.error != null && <ErrorBox error={patchParticipant.error} />}
        </div>
      );
    }
    return <EmptyState>{t("event.notFound")}</EmptyState>;
  }

  const participants = event.eventParticipantsByEventId.nodes;
  const myRow = (personId && participants.find((p) => p.personId === personId)) || null;
  const organizers = participants.filter((p) => p.role === "ORGANIZER");
  const confirmed = participants.filter((p) => p.status === "CONFIRMED");
  const pendingRows = participants.filter((p) => p.status === "PENDING");
  const invitedRows = participants.filter((p) => p.status === "INVITED");

  const isCreator = personId != null && event.createdBy === personId;
  const isOrganizer = isCreator || (myRow?.role === "ORGANIZER" && myRow.status === "CONFIRMED");
  const clubRow = event.clubId
    ? (memberships.data?.clubMembers.nodes.find((m) => m.clubId === event.clubId) ?? null)
    : null;
  const isClubMember = clubRow?.status === "ACTIVE";
  const isClubAdmin = isClubMember && (clubRow!.role === "OWNER" || clubRow!.role === "ADMIN");
  const canManage = isOrganizer || isClubAdmin;

  const routes = event.eventRoutesByEventId.nodes;
  const cap = event.maxParticipants;
  const isFull = cap != null && confirmed.length >= cap;

  const actionError =
    rsvp.error ?? patchParticipant.error ?? removeParticipant.error ?? invite.error ?? deleteEvent.error;

  const statusChip = (label: string) => (
    <Badge variant="secondary" className="text-muted-foreground">
      {label}
    </Badge>
  );
  const note = (text: ReactNode) => <p className="text-sm text-muted-foreground">{text}</p>;

  const membersOnlyNote = note(
    <>
      {t("event.membersOnlyPre")}{" "}
      {event.club ? (
        <Link href={`/group/${event.club.id}`} className="font-semibold text-primary hover:underline">
          {event.club.name}
        </Link>
      ) : (
        t("event.theGroup")
      )}{" "}
      {t("event.membersOnlyPost")}
    </>,
  );

  const rsvpControl = (() => {
    if (myRow) {
      if (myRow.role === "ORGANIZER") return statusChip(labelizeT("ORGANIZER"));
      switch (myRow.status) {
        case "PENDING":
          return (
            <span className="inline-flex items-center gap-2">
              {statusChip(t("event.requestPending"))}
              <Button
                type="button"
                variant="outline"
                onClick={() => removeParticipant.mutate(myRow.id)}
                disabled={removeParticipant.isPending}
              >
                {t("event.withdrawRequest")}
              </Button>
            </span>
          );
        case "INVITED":
          return (
            <span className="inline-flex items-center gap-2">
              <Button
                type="button"
                onClick={() => patchParticipant.mutate({ participantId: myRow.id, status: "CONFIRMED" })}
                disabled={patchParticipant.isPending}
              >
                {t("event.acceptInvite")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => patchParticipant.mutate({ participantId: myRow.id, status: "DECLINED" })}
                disabled={patchParticipant.isPending}
              >
                {t("group.decline")}
              </Button>
            </span>
          );
        case "CONFIRMED":
          return (
            <span className="inline-flex items-center gap-2">
              {statusChip(t("event.youAreGoing"))}
              <Button
                type="button"
                variant="outline"
                onClick={() => patchParticipant.mutate({ participantId: myRow.id, status: "DECLINED" })}
                disabled={patchParticipant.isPending}
              >
                {t("event.cantGo")}
              </Button>
            </span>
          );
        case "DECLINED":
          return (
            <span className="inline-flex items-center gap-2">
              {statusChip(t("event.youDeclined"))}
              <Button
                type="button"
                onClick={() => patchParticipant.mutate({ participantId: myRow.id, status: "CONFIRMED" })}
                disabled={patchParticipant.isPending}
              >
                {t("event.imGoing")}
              </Button>
            </span>
          );
      }
    }
    if (!personId) {
      // Anonymous visitors get a login link, not a disabled button.
      if (event.joinPolicy === "OPEN" || event.joinPolicy === "REQUEST_TO_JOIN")
        return (
          <Button asChild variant="outline">
            <Link href={`/login?next=${encodeURIComponent(`/event/${id}`)}`}>
              {event.joinPolicy === "OPEN" ? t("event.join") : t("event.requestJoin")}
            </Link>
          </Button>
        );
      if (event.joinPolicy === "MEMBERS_ONLY") return membersOnlyNote;
      if (event.joinPolicy === "INVITE_ONLY") return note(t("event.inviteOnlyNote"));
      return note(t("event.closedNote"));
    }
    switch (event.joinPolicy) {
      case "OPEN":
        return (
          <Button type="button" onClick={() => rsvp.mutate("CONFIRMED")} disabled={rsvp.isPending}>
            {t("event.join")}
          </Button>
        );
      case "REQUEST_TO_JOIN":
        return (
          <Button type="button" onClick={() => rsvp.mutate("PENDING")} disabled={rsvp.isPending}>
            {t("event.requestJoin")}
          </Button>
        );
      case "MEMBERS_ONLY":
        // The server verifies membership — the UI just avoids offering a
        // button that would fail.
        if (isClubMember)
          return (
            <Button type="button" onClick={() => rsvp.mutate("CONFIRMED")} disabled={rsvp.isPending}>
              {t("event.join")}
            </Button>
          );
        return membersOnlyNote;
      case "INVITE_ONLY":
        return note(t("event.inviteOnlyNote"));
      case "CLOSED":
        return note(t("event.closedNote"));
    }
  })();

  const header = (
    <div>
      <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
        <CalendarDays aria-hidden className="size-3.5" />
        {t("event.kicker")}
        {event.activities && event.activities.length > 0 && (
          <>
            {" · "}
            <ActivityList activities={event.activities} />
          </>
        )}
        {event.club && (
          <Link
            href={`/group/${event.club.id}`}
            className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 normal-case tracking-normal text-primary hover:underline"
          >
            {event.club.name}
          </Link>
        )}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{event.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              downloadEventIcs({
                id: event.id,
                title: event.title,
                description: event.description,
                startsAt: event.startsAt,
                endsAt: event.endsAt,
                location:
                  event.place?.name ??
                  (() => {
                    const c = pointCoords(event.location);
                    return c ? `${c[1].toFixed(5)}, ${c[0].toFixed(5)}` : null;
                  })(),
                recurrence: event.recurrence,
                recurrenceUntil: event.recurrenceUntil,
              })
            }
          >
            <CalendarPlus aria-hidden className="size-3.5" />
            {t("event.addToCalendar")}
          </Button>
          <ShareButton title={event.title} />
          {canManage && (
            <>
              <Button asChild variant="outline">
                <Link href={`/event/${event.id}/edit`}>{t("event.edit")}</Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (window.confirm(t("event.confirmDelete"))) deleteEvent.mutate();
                }}
                disabled={deleteEvent.isPending}
                className="border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
              >
                {deleteEvent.isPending ? t("common.deleting") : t("common.delete")}
              </Button>
            </>
          )}
        </div>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{formatDateRange(event.startsAt, event.endsAt)}</span>
        {event.recurrence !== "NONE" && (
          <span>
            ·{" "}
            {event.recurrenceUntil
              ? t("event.repeatsUntil", { freq: labelizeT(event.recurrence), date: formatDate(event.recurrenceUntil) })
              : t("event.repeats", { freq: labelizeT(event.recurrence) })}
          </span>
        )}
        {statusChip(labelizeT(event.visibility))}
        {statusChip(labelizeT(event.joinPolicy))}
      </p>
      {organizers.length > 0 && (
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          {t("event.organizedBy")}
          {organizers.map((o) => (
            <Link
              key={o.id}
              href={`/person/${o.personId}`}
              className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
            >
              <Avatar person={{ displayName: o.person?.displayName ?? null, avatarKey: o.person?.avatarKey }} size="xs" />
              {o.person?.displayName ?? t("common.someone")}
            </Link>
          ))}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {rsvpControl}
        <span className="text-sm text-muted-foreground">
          {cap != null ? t("event.goingCap", { count: confirmed.length, cap }) : t("event.goingCount", { count: confirmed.length })}
          {isFull && <span className="ml-1.5 font-medium text-amber-600 dark:text-amber-400">{t("event.fullHint")}</span>}
        </span>
      </div>
      {actionError != null && <ErrorBox error={actionError} />}
    </div>
  );

  const infoTab = (
    <div className="space-y-4">
      {event.description ? (
        <p className="whitespace-pre-line text-foreground">{event.description}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{t("event.noDescription")}</p>
      )}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {event.activities && event.activities.length > 0 && (
          <Stat label={t("form.activities")} value={event.activities.map(labelizeT).join(", ")} />
        )}
        <Stat label={t("group.joinPolicy")} value={labelizeT(event.joinPolicy)} />
        <Stat label={t("form.visibility")} value={labelizeT(event.visibility)} />
        {cap != null && <Stat label={t("event.maxParticipants")} value={String(cap)} />}
        {event.recurrence !== "NONE" && <Stat label={t("event.recurrence")} value={labelizeT(event.recurrence)} />}
      </dl>
      {event.place && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin aria-hidden className="size-3.5" />
          {t("event.meetingPoint")}:{" "}
          <Link href={`/place/${event.place.id}`} className="font-medium text-primary hover:underline">
            {event.place.name}
          </Link>
          <span className="text-xs">({labelizeT(event.place.type)})</span>
        </p>
      )}
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <CalendarDays aria-hidden className="size-3.5" />
        {t("group.created", { date: formatDate(event.createdAt.slice(0, 10)) })}
      </p>
    </div>
  );

  const routesTab = routes.length ? (
    <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
      {routes.map((r) => (
        <li key={r.id}>
          <Link
            href={`/route/${r.route.id}`}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
          >
            <Compass aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="block font-medium text-foreground">{entityName(r.route)}</span>
              <span className="block text-xs text-muted-foreground">
                {r.route.activities.map(labelizeT).join(", ")}
                {r.route.durationMin != null ? ` · ${formatDuration(r.route.durationMin)}` : ""}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  ) : (
    <EmptyState>{t("event.noRoutes")}</EmptyState>
  );

  const participantsTab = (
    <div className="space-y-5">
      {organizers.length > 0 && (
        <ParticipantSection title={t("event.organizers")}>
          {organizers.map((p) => (
            <ParticipantRow key={p.id} participant={p} />
          ))}
        </ParticipantSection>
      )}

      <ParticipantSection title={t("event.goingSection")}>
        {confirmed.filter((p) => p.role !== "ORGANIZER").length > 0 ? (
          confirmed
            .filter((p) => p.role !== "ORGANIZER")
            .map((p) => (
              <ParticipantRow
                key={p.id}
                participant={p}
                actions={
                  isOrganizer && p.personId !== personId ? (
                    <ParticipantAction
                      label={t("common.remove")}
                      destructive
                      onClick={() => {
                        if (window.confirm(t("event.confirmRemove", { name: p.person?.displayName ?? t("common.someone") })))
                          removeParticipant.mutate(p.id);
                      }}
                      disabled={removeParticipant.isPending}
                    />
                  ) : null
                }
              />
            ))
        ) : (
          <li className="px-4 py-3 text-sm text-muted-foreground">{t("event.nobodyYet")}</li>
        )}
      </ParticipantSection>

      {isOrganizer && pendingRows.length > 0 && (
        <ParticipantSection title={t("group.pendingRequests")}>
          {pendingRows.map((p) => (
            <ParticipantRow
              key={p.id}
              participant={p}
              actions={
                <>
                  <ParticipantAction
                    label={t("group.approve")}
                    onClick={() => patchParticipant.mutate({ participantId: p.id, status: "CONFIRMED" })}
                    disabled={patchParticipant.isPending}
                  />
                  <ParticipantAction
                    label={t("group.decline")}
                    destructive
                    onClick={() => removeParticipant.mutate(p.id)}
                    disabled={removeParticipant.isPending}
                  />
                </>
              }
            />
          ))}
        </ParticipantSection>
      )}

      {isOrganizer && invitedRows.length > 0 && (
        <ParticipantSection title={labelizeT("INVITED")}>
          {invitedRows.map((p) => (
            <ParticipantRow
              key={p.id}
              participant={p}
              actions={
                <ParticipantAction
                  label={t("common.remove")}
                  destructive
                  onClick={() => removeParticipant.mutate(p.id)}
                  disabled={removeParticipant.isPending}
                />
              }
            />
          ))}
        </ParticipantSection>
      )}

      {isOrganizer && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-foreground">{t("group.invite")}</h3>
          {/* Picking a person invites them immediately — the picker's selected
              list stays empty on purpose (same as the group invite). */}
          <PersonPicker
            selected={[]}
            onChange={(next) => {
              const p = next[next.length - 1];
              if (p) invite.mutate(p);
            }}
            excludeIds={participants.map((p) => p.personId)}
          />
        </div>
      )}
    </div>
  );

  const mapFeatures = [
    toFeature(event.location ?? event.place?.geom ?? null, { id: "event-point" }),
    ...routes.map((r) => toFeature(r.route.mainGeom, { id: r.route.id })),
  ];

  const mapTab = mapFeatures.some(Boolean) ? (
    <MiniMap
      features={mapFeatures}
      className="h-96 w-full overflow-hidden rounded-xl border shadow-card"
    />
  ) : (
    <EmptyState>{t("event.noGeometry")}</EmptyState>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.info"), content: infoTab },
        { key: "routes", label: t("tabs.routes"), badge: routes.length, content: routesTab },
        { key: "participants", label: t("tabs.participants"), badge: confirmed.length, content: participantsTab },
        { key: "map", label: t("tabs.map"), content: mapTab },
      ]}
    />
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-muted/50 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
}

function ParticipantSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
      <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">{children}</ul>
    </div>
  );
}

function ParticipantRow({ participant, actions }: { participant: EventParticipantNode; actions?: ReactNode }) {
  const { t } = useT();
  return (
    <li className="flex flex-wrap items-center gap-2 px-4 py-3">
      <Avatar
        person={{ displayName: participant.person?.displayName ?? null, avatarKey: participant.person?.avatarKey }}
        size="sm"
      />
      <Link href={`/person/${participant.personId}`} className="font-medium text-primary hover:underline">
        {participant.person?.displayName ?? t("common.someone")}
      </Link>
      {actions != null && <span className="ml-auto flex flex-wrap items-center gap-1.5">{actions}</span>}
    </li>
  );
}

function ParticipantAction({
  label,
  onClick,
  disabled,
  destructive,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      onClick={onClick}
      disabled={disabled}
      className={
        destructive
          ? "border-destructive/30 font-medium text-destructive hover:bg-destructive/10 hover:text-destructive"
          : "font-medium text-muted-foreground"
      }
    >
      {label}
    </Button>
  );
}
